/**
 * Compiles the English lexicon data (ENGINE-SPEC §5) into matchers:
 *   - TELLS and HEDGES to id-set style word sets,
 *   - PHRASES followed by SCAFFOLDS to one Aho-Corasick automaton,
 *   - every regex list (CONTRAST_RES, TROPE_RES, BRAND_RE, GROWTH_RE, TRIAD_RE, SCAFFOLD_RE,
 *     CONTRACTION_RE, the OB-01 chat-citation patterns and the channel literals) to a
 *     `RegexList` with declared prefilters.
 *
 * Regexes are compiled from the frozen seed `source` and `flags`; the only rewrites are the
 * registered v3 `overrides` (candidate change C-LEX-BT), which must keep the hit set.
 */

import type {
	ChannelFile,
	EnglishLexicons,
	NamedPatternsData,
	Ob01AiciteData,
	RegexData,
} from '../lang/en/lexicons/index.ts';
import { PhraseMatcher } from './phrase-matcher.ts';
import { RegexList, type RegexSpec } from './regex-list.ts';
import { WordSet } from './word-set.ts';

export interface NamedRegexes {
	list: RegexList;
	/** Position of each named pattern in `list`. */
	index: Readonly<Record<string, number>>;
	/** Word lists of the same file. */
	lists: Readonly<Record<string, readonly string[]>>;
	get(name: string): RegExp;
}

export interface CompiledEnglish {
	lexiconVersion: string;
	tells: WordSet;
	hedges: WordSet;
	/** PHRASES at indices [0, phraseCount), then SCAFFOLDS at [phraseCount, phraseCount + n). */
	phrases: PhraseMatcher;
	phraseCount: number;
	scaffoldCount: number;
	contrast: RegexList;
	trope: RegexList;
	brand: RegexList;
	growth: RegexList;
	triad: RegexList;
	scaffoldRe: RegexList;
	contraction: RegexList;
	ob01Aicite: RegexList;
	channels: Record<ChannelFile, NamedRegexes>;
	segLean: NamedRegexes;
	tokenize: NamedRegexes;
	processPack: NamedRegexes;
}

const escapeLiteral = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export function compileNamed(
	data: NamedPatternsData,
	prefilters?: Readonly<Record<string, readonly string[]>>,
	rewrite?: (name: string, re: RegexData) => RegexData
): NamedRegexes {
	const names = Object.keys(data.patterns);
	const specs: RegexSpec[] = names.map((name) => {
		const base = data.patterns[name] as RegexData;
		const re = rewrite ? rewrite(name, base) : base;
		return { source: re.source, flags: re.flags, prefilter: prefilters?.[name] };
	});
	const list = new RegexList(specs);
	const index: Record<string, number> = {};
	names.forEach((n, i) => {
		index[n] = i;
	});
	return {
		list,
		index,
		lists: data.lists,
		get(name: string): RegExp {
			const i = index[name];
			if (i === undefined) throw new Error(`unknown pattern ${name}`);
			return list.regexes[i] as RegExp;
		},
	};
}

function compileOb01(data: Ob01AiciteData): RegexList {
	return new RegexList(
		data.patterns.map((p) => ({
			source: p.kind === 'literal' ? escapeLiteral(p.pattern) : p.pattern,
			flags: `g${p.flags}`,
			prefilter: p.prefilter,
		}))
	);
}

export function compileEnglishLexicons(lex: EnglishLexicons): CompiledEnglish {
	const pf = lex.prefilters;
	const overrides = lex.overrides.entries;
	const single = (id: string, re: RegexData): RegexData => {
		const o = overrides.find((e) => e.target === id && e.index === undefined);
		return o ? { source: o.source, flags: o.flags } : re;
	};
	const many = (id: string, res: readonly RegexData[]): RegexSpec[] =>
		res.map((re, i) => {
			const o = overrides.find((e) => e.target === id && e.index === i);
			const r = o ? { source: o.source, flags: o.flags } : re;
			return { source: r.source, flags: r.flags, prefilter: pf.lists[id]?.[i] };
		});
	const one = (id: string, re: RegexData): RegexList => {
		const r = single(id, re);
		return new RegexList([{ source: r.source, flags: r.flags, prefilter: pf.lists[id]?.[0] }]);
	};

	const byTarget =
		(file: string) =>
		(name: string, re: RegexData): RegexData => {
			const o = overrides.find((e) => e.target === `${file}.${name}`);
			return o ? { source: o.source, flags: o.flags } : re;
		};
	const channelNames = Object.keys(lex.channels) as ChannelFile[];
	const channels = {} as Record<ChannelFile, NamedRegexes>;
	for (const f of channelNames) {
		channels[f] = compileNamed(lex.channels[f], pf.channels[f], byTarget(f));
	}

	return {
		lexiconVersion: lex.lexiconVersion,
		tells: new WordSet(lex.tells.entries),
		hedges: new WordSet(lex.hedges.entries),
		phrases: new PhraseMatcher([...lex.phrases.entries, ...lex.scaffolds.entries]),
		phraseCount: lex.phrases.entries.length,
		scaffoldCount: lex.scaffolds.entries.length,
		contrast: new RegexList(many('contrast-res', lex.contrastRes.entries)),
		trope: new RegexList(many('trope-res', lex.tropeRes.entries)),
		brand: one('brand-re', lex.brandRe.re),
		growth: one('growth-re', lex.growthRe.re),
		triad: one('triad-re', lex.triadRe.re),
		scaffoldRe: one('scaffold-re', lex.scaffoldRe.re),
		contraction: one('contraction-re', lex.contractionRe.re),
		ob01Aicite: compileOb01(lex.ob01Aicite),
		channels,
		segLean: compileNamed(lex.functions.segLean, undefined, byTarget('fn-seglean')),
		tokenize: compileNamed(lex.functions.tokenize, undefined, byTarget('fn-tokenize')),
		processPack: compileNamed(lex.processPack, undefined, byTarget('pack-process')),
	};
}
